<template>
  <span v-if="!!data.parameters" style="flex-grow: 1">
    <v-row
      no-gutters
      v-if="isEditingOn && currentMetadata.type === 'Monitoring'"
      class="align-baseline"
      style="gap: 2rem"
    >
      <v-col cols="auto"> {{ $i18n.t('table.rziMonitoring.scoreDate') }}: </v-col>
      <v-col cols="auto">
        <data-input
          v-model="data['main'].properties[metadataAttributesMapping.main.scoreDate]"
          :rules="[v => !!v || v == '0' || 'rules.required']"
          :dataType="{ name: 'date' }"
          prependInnerIcon="mdi-calendar"
        />
      </v-col>
    </v-row>
    <v-expansion-panels v-model="panel" multiple accordion focusable>
      <v-expansion-panel
        v-for="(parameter, index) in isEditingOn
          ? computedScoreStructure
          : computedScoreStructure.filter(param => !!data.parameters[param.feature?.id]?.id || param.isMain)"
        :readonly="!parameter.indexes.length"
        :key="index"
      >
        <v-expansion-panel-header hide-actions>
          <v-row no-gutters class="align-center" style="position: relative;">
            <v-col :class="isEditingOn ? 'editing-grid' : 'view-grid'">
              <div class="title grid-item text-body-2" :class="{ 'font-weight-medium': parameter.isMain }">
                {{ parameter.title }}
              </div>
              <div v-if="isEditingOn ? parameter.indexes.length : true" class="arrow ml-2">
                <v-icon
                  :class="{ 'is-panel-closed': !panel.includes(index), 'arrow-noelements': !parameter.indexes.length }"
                >
                  mdi-chevron-up
                </v-icon>
              </div>
              <div class="score grid-item d-flex justify-end">
                <data-input
                  v-if="parameter.isMain"
                  @click.stop
                  @change="changeParameterScore({ type: 'main' })"
                  v-bind="getScoreInputProps(data['main'])"
                  v-model="data.main.properties[metadataAttributesMapping.main.score]"
                />
                <data-input
                  v-else
                  @click.stop
                  @change="changeParameterScore({ type: 'parameters' })"
                  v-bind="getScoreInputProps(data['parameters'][parameter.feature.id])"
                  v-model="
                    data['parameters'][parameter.feature.id].properties[metadataAttributesMapping.parameters.score]
                  "
                />
              </div>
              <div class="description grid-item">
                <div v-if="!isEditingOn" class="text-caption flex-grow-1">
                  {{
                    parameter.isMain
                      ? data['main'].properties[metadataAttributesMapping.main.description]
                      : data['parameters'][parameter.feature?.id].properties[
                          metadataAttributesMapping.parameters.description
                        ]
                  }}
                </div>
                <div v-else @click.stop @keyup.prevent class="flex-grow-1">
                  <v-row no-gutters style="gap: 1rem">
                    <v-col :cols="12">
                      <data-input
                        v-if="parameter.isMain"
                        v-model="data['main'].properties[metadataAttributesMapping.main.description]"
                        :rules="[v => !!v || v == '0' || 'rules.required']"
                        v-bind="descriptionInput"
                      />
                      <data-input
                        v-else
                        v-model="
                          data['parameters'][parameter.feature?.id].properties[
                            metadataAttributesMapping.parameters.description
                          ]
                        "
                        :rules="[v => !!v || v == '0' || 'rules.required']"
                        v-bind="descriptionInput"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content v-if="parameter.indexes.length">
          <v-list class="indexes-list my-4 mx-2">
            <template
              v-for="(item, index) in isEditingOn
                ? parameter.indexes
                : parameter.indexes.filter(
                    ind =>
                      data.indexes[ind.feature.id].properties[currentMetadata.score.indexes.attributes.score] ?? true
                  )"
            >
              <v-list-item :key="item.title" class="indexes-list-item">
                <template>
                  <v-list-item-content class="text-left">
                    <v-row no-gutters>
                      <v-col :class="isEditingOn ? 'editing-grid-indexes' : 'view-grid-indexes'">
                        <div class="title">
                          <v-row no-gutters>
                            <v-col class="text-body-2"
                              >{{ item.title }}
                              <span v-if="isEditingOn">
                                {{
                                  item.feature.properties[currentMetadata.score.dictRawIndexes.attributes.required]
                                    ? '*'
                                    : ''
                                }}</span
                              >
                            </v-col>
                          </v-row>
                          <v-row
                            v-if="
                              isEditingOn &&
                                item.feature.properties[currentMetadata.score.dictRawIndexes.attributes.cardinal]
                            "
                            no-gutters
                          >
                            <v-col class="text-caption">{{ $i18n.t('table.rzi.cardinal') }}</v-col>
                          </v-row>
                        </div>
                        <div
                          v-if="currentMetadata.score.indexes.attributes.score"
                          class="score d-flex justify-end align-center"
                        >
                          <data-input
                            @click.stop
                            v-bind="getScoreInputProps(data['indexes'][item.feature?.id], 'indexes')"
                            @change="
                              changeIndexScore({ type: 'parameters', feature: parameter.feature }, parameter.indexes)
                            "
                            v-model="
                              data['indexes'][item.feature?.id].properties[metadataAttributesMapping.indexes.score]
                            "
                          />
                        </div>

                        <div class="description">
                          <v-row no-gutters class="align-center">
                            <v-col class="d-flex flex-column" style="gap: 0.5rem; flex-grow: 1">
                              <v-row no-gutters class="mb-2">
                                <v-col>
                                  <template v-if="isEditingOn">
                                    <v-row
                                      v-if="
                                        item.feature.properties[
                                          currentMetadata.score.dictRawIndexes.attributes.cardinalDesc
                                        ]
                                      "
                                      no-gutters
                                    >
                                      <v-col class="text-caption font-italic text--secondary"
                                        >{{ $i18n.t('table.rzi.cardinality') }}:
                                        {{
                                          item.feature.properties[
                                            currentMetadata.score.dictRawIndexes.attributes.cardinalDesc
                                          ]
                                        }}</v-col
                                      >
                                    </v-row>
                                    <v-row
                                      v-if="
                                        item.feature.properties[
                                          currentMetadata.score.dictRawIndexes.attributes.requireDesc
                                        ]
                                      "
                                      no-gutters
                                    >
                                      <v-col class="text-caption font-italic text--secondary"
                                        >{{ $i18n.t('table.rzi.requirements') }}:
                                        {{
                                          item.feature.properties[
                                            currentMetadata.score.dictRawIndexes.attributes.requireDesc
                                          ]
                                        }}</v-col
                                      >
                                    </v-row>
                                  </template>
                                </v-col>
                              </v-row>
                              <v-row no-gutters>
                                <v-col v-if="!isEditingOn" cols="auto" class="text-caption">
                                  {{
                                    data.indexes[item.feature.id].properties[
                                      metadataAttributesMapping.indexes.description
                                    ]
                                  }}
                                </v-col>
                                <v-col cols="12" v-else>
                                  <v-row>
                                    <v-col v-if="currentMetadata.score.indexes.attributes.value" cols="6" @click.stop>
                                      <data-input
                                        v-model="
                                          data.indexes[item.feature.id].properties[
                                            metadataAttributesMapping.indexes.value
                                          ]
                                        "
                                        v-bind="valueInput"
                                      />
                                    </v-col>
                                    <v-col :cols="currentMetadata.score.indexes.attributes.value ? 6 : 12" @click.stop>
                                      <data-input
                                        v-model="
                                          data.indexes[item.feature.id].properties[
                                            metadataAttributesMapping.indexes.description
                                          ]
                                        "
                                        v-bind="descriptionInput"
                                      />
                                    </v-col>
                                  </v-row>
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </template>
              </v-list-item>
              <v-divider v-if="index < parameter.indexes.length - 1" :key="index"></v-divider>
            </template>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </span>
</template>

<script>
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { get } from 'vuex-pathify';

export default {
  name: 'RziScoresList',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    DataInput,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    currentMetadata: {
      type: Object,
    },
    scoresStructure: {
      type: Array,
      required: true,
    },
    structure: {
      type: Object,
    },
    isEditingOn: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    panel: [],
    data: {
      main: null,
      parameters: null,
      indexes: null,
    },
  }),
  watch: {
    structure: {
      deep: true,
      immediate: true,
      handler() {
        this.prepareData();
      },
    },
    isVisible(nV) {
      if (nV) this.prepareData();
    },
  },
  computed: {
    scores: get('rziData/scores'),
    computedScoreStructure() {
      return [
        ...this.scoresStructure,
        {
          title: 'Ocena ogólna',
          isMain: true,
          indexes: [],
        },
      ];
    },
    metadataAttributesMapping() {
      return {
        main:
          this.currentMetadata.type === 'Monitoring'
            ? {
                score: this.currentMetadata.monitoringYears?.attributes.score,
                description: this.currentMetadata.monitoringYears?.attributes.scoreDesc,
                scoreDate: this.currentMetadata.monitoringYears?.attributes.scoreDate,
              }
            : {
                score: this.currentMetadata.protectedSites?.attributes.gios_score,
                description: this.currentMetadata.protectedSites?.attributes.notes,
              },
        parameters: {
          score: this.currentMetadata.score.parameters.attributes.score,
          description: this.currentMetadata.score.parameters.attributes.description,
        },
        indexes: {
          description: this.currentMetadata.score.indexes.attributes.description,
          score: this.currentMetadata.score.indexes.attributes.score,
          value: this.currentMetadata.score.indexes.attributes.value,
        },
      };
    },
    descriptionInput() {
      return {
        dataType: {
          name: 'text',
        },
        name: 'description',
        hideNameColumn: true,
        hideDetails: true,
        cols: 12,
        label: this.$i18n.t('table.rziDocuments.scoreDescription'),
      };
    },
    valueInput() {
      return {
        dataType: {
          name: 'text',
        },
        name: 'value',
        hideNameColumn: true,
        hideDetails: true,
        cols: 12,
        label: this.$i18n.t('table.rziMonitoring.valueIndex'),
      };
    },
    statusInput() {
      return {
        dataType: {
          name: 'selectcolor',
        },
        name: 'status',
        hideNameColumn: true,
        hideDetails: true,
        itemValue: 'value',
        cols: 12,
        clearable: false,
        translationPath: 'dialog.status',
      };
    },
  },
  methods: {
    getScoreInputProps(ft, type) {
      let rules;
      if (type === 'indexes') {
        const matchedData = this.structure.rawIndexes.find(
          searchedIndex =>
            searchedIndex.properties[this.currentMetadata.score.indexes.attributes.title] ===
            ft.properties[this.currentMetadata.score.indexes.attributes.title]
        );
        if (!matchedData) {
          rules = [];
        } else {
          const isRequired = matchedData.properties[this.currentMetadata.score.dictRawIndexes.attributes.required];
          rules = isRequired ? [v => !!v || v == '0' || 'rules.required'] : [];
        }
      } else {
        rules = [v => !!v || v == '0' || 'rules.required'];
      }
      return {
        ...this.statusInput,
        rules: rules,
        items: ft.availableScores
          ? this.scores
              .filter(sc => ft.availableScores.includes(sc.id))
              .map(sc => ({
                text: sc.code,
                value: sc.id,
              }))
          : this.scores.map(sc => ({
              text: sc.code,
              value: sc.id,
            })),
        colors: ft.availableScores?.length
          ? this.scores.filter(sc => ft.availableScores.includes(sc.id)).map(sc => sc.color)
          : this.scores.map(sc => sc.color),
        isEditingOn: this.isEditingOn,
        readonly: !this.isEditingOn,
      };
    },
    changeIndexScore(data, items) {
      const scoreProperty = this.currentMetadata.score.parameters.attributes.score;
      const currentParameterScore = this.data.parameters[data.feature.id].properties[scoreProperty];
      const newAvailableScores = this.getAvailableScores({ type: 'parameters', feature: data.feature }, items);
      if (!newAvailableScores.includes(currentParameterScore)) {
        this.data.parameters[data.feature.id].properties[scoreProperty] = this.data.parameters[data.feature.id]
          .properties[scoreProperty]
          ? newAvailableScores[0]
          : null;
        this.changeParameterScore({ type: 'parameters' });
      }
      this.data.parameters[data.feature.id].availableScores = newAvailableScores;
    },
    changeParameterScore(data) {
      if (data.type === 'main') return;
      const mainScoreAttributes = this.metadataAttributesMapping.main.score;
      let currentMainScore = this.data.main.properties[mainScoreAttributes];
      const newAvailableMainScores = this.getAvailableScores({ type: 'main' }, this.scoresStructure);
      if (!newAvailableMainScores.includes(currentMainScore))
        this.data.main.properties[mainScoreAttributes] = newAvailableMainScores[0];
      this.data.main.availableScores = newAvailableMainScores;
    },
    getAvailableScores(data, items) {
      switch (data.type) {
        case 'main': {
          const mappedParameterScores = (Object.values(this.data?.parameters || []) || []).map(
            parameter => parameter?.properties[this.metadataAttributesMapping.parameters.score]
          );
          return mappedParameterScores.some(score => !!score)
            ? this.scores
                .filter(sc => sc.id >= mappedParameterScores.reduce((a, b) => Math.max(a, b), -Infinity))
                .map(sc => sc.id) || []
            : [];
        }
        case 'parameters': {
          const matchedIndexes = items.reduce((accInd, ind) => {
            if (!this.data.indexes[ind.feature.id]) return accInd;
            const indexScore =
              this.data.indexes[ind.feature.id]?.properties[this.metadataAttributesMapping.indexes.score] ?? null;
            if (!indexScore) return accInd;
            accInd.push({
              required: ind.feature.properties[this.currentMetadata.score.dictRawIndexes.attributes.required],
              cardinal: ind.feature.properties[this.currentMetadata.score.dictRawIndexes.attributes.cardinal],
              score: indexScore,
            });
            return accInd;
          }, []);
          const cardinalScores = matchedIndexes.filter(ind => ind.cardinal);
          const maxScore = (cardinalScores.length ? cardinalScores : matchedIndexes)
            .map(csc => csc.score)
            .reduce((a, b) => Math.max(a, b), -Infinity);
          if (cardinalScores.length) {
            return this.scores.filter(sc => sc.id === maxScore).map(sc => sc.id) || [];
          }
          return this.scores.filter(sc => sc.id >= maxScore).map(sc => sc.id) || [];
        }
        default:
          return [];
      }
    },
    prepareData() {
      this.panel = [...Array(this.scoresStructure.length).fill(null)].map((k, i) => i);
      this.data = this.scoresStructure.reduce(
        (acc, item) => {
          /**
           * Parameters
           */
          if (!acc.parameters) acc.parameters = {};
          const parameterAttributes = this.currentMetadata.score.parameters.attributes;
          const matchedParameter = this.structure.scoreParameters.find(
            parameter => parameter.properties[parameterAttributes.title] === item.feature.id
          );
          acc.parameters[item.feature.id] = {
            id: matchedParameter?.id ?? null,
            properties: matchedParameter
              ? JSON.parse(JSON.stringify(matchedParameter.properties))
              : {
                  [this.currentMetadata.score.parameters.filterAttribute]: this.item.id,
                  [parameterAttributes.score]: null,
                  [parameterAttributes.title]: item.feature.id,
                  [parameterAttributes.description]: null,
                },
          };
          /**
           * Indexes
           */
          const indexes = item.indexes;
          if (indexes.length) {
            if (!acc.indexes) acc.indexes = {};
            const indexesAttributes = this.currentMetadata.score.indexes.attributes;
            indexes.forEach(index => {
              const matchedIndex = this.structure.scoreIndexes.find(
                searchedIndex =>
                  searchedIndex.properties[indexesAttributes.title] ===
                  index.feature.properties[this.currentMetadata.score.dictRawIndexes.attributes.name]
              );
              acc.indexes[index.feature.id] = {
                id: matchedIndex?.id ?? null,
                ...(this.currentMetadata.score.indexes.attributes.bindedParameter
                  ? {
                      rawParameterId:
                        index.feature.properties[this.currentMetadata.score.dictRawIndexes.attributes.parameter],
                      rawParameterIdName: this.getAttributeTextValueByName(
                        index.feature.properties[this.currentMetadata.score.dictRawIndexes.attributes.parameter],
                        this.currentMetadata.score.dictRawIndexes.attributes.parameter,
                        this.currentMetadata.score.dictRawIndexes.datasource
                      ),
                    }
                  : {
                      rawParentId: this.item.id,
                    }),
                properties: matchedIndex
                  ? JSON.parse(JSON.stringify(matchedIndex.properties))
                  : {
                      ...(indexesAttributes.score && { [indexesAttributes.score]: null }),
                      ...(indexesAttributes.value && { [indexesAttributes.value]: null }),
                      [indexesAttributes.title]: index.feature.properties[indexesAttributes.title],
                      [indexesAttributes.bindedParameter ?? indexesAttributes.bindedParent]: null,
                      [indexesAttributes.description]: null,
                    },
              };
            });
          }
          return acc;
        },
        {
          main: {
            availableScores: [],
            properties: JSON.parse(JSON.stringify(this.structure.main)),
          },
        }
      );
      this.data.main.availableScores = this.getAvailableScores({ type: 'main' }, this.scoresStructure);
      if (!this.currentMetadata.score.indexes.attributes.score) return;
      this.scoresStructure
        .filter(parameter => parameter.indexes?.length)
        .forEach(parameter => {
          this.data.parameters[parameter.feature.id].availableScores = this.getAvailableScores(
            { type: 'parameters', feature: parameter.feature },
            parameter.indexes
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.is-panel-closed {
  transform: rotate(-180deg);
}
.title {
  grid-area: title;
}
.score {
  grid-area: score;
}
.description {
  grid-area: description;
}
.grid-item {
  display: flex !important;
  align-items: center;
}
.arrow {
  grid-area: arrow;
  display: flex;
  align-items: center;
}
.editing-grid-indexes {
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: 0.5rem;
  grid-template-areas:
    'title score'
    'description description';
}
.view-grid-indexes {
  display: grid;
  grid-template-columns: 1fr auto;
  row-gap: 0.5rem;
  grid-template-areas:
    'title score'
    'description score';
}
.editing-grid {
  display: grid;
  grid-template-columns: 1fr auto auto;
  row-gap: 1rem;
  grid-template-areas:
    'title score arrow'
    'description description description';
}

.view-grid {
  display: grid;
  grid-template-columns: 1fr auto auto;
  row-gap: 1rem;
  grid-template-areas:
    'title score arrow'
    'description score arrow';
}

:deep(.v-expansion-panel-header:before) {
  background-color: unset !important;
}
:deep(.v-expansion-panel-header__icon) {
  margin-left: 0.5rem;
}
.arrow-noelements {
  visibility: hidden !important;
}
:deep(.select-color span),
:deep(.select-color div) {
  font-size: 12px !important;
}
:deep(.v-text-field) {
  background-color: #fff;
}
:deep(.indexes-list) {
  padding: 0;
  border-radius: 4px;
  @include border(all, 1px);
}
:deep(.indexes-list-item) {
  background-color: rgb(231, 231, 231, 0.5);
}
:deep(.networkRow) {
  display: flex;
  padding: 0 !important;
}
</style>
